import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';

export interface FooterLink {
  text: string;
  url: string;
}

export interface FooterSection {
  title: string;
  links: FooterLink[];
}

export interface SocialMedia {
  platform: string;
  url: string;
  enabled: boolean;
}

export interface FooterSettings {
  company_name: string;
  description: string;
  email: string;
  copyright_text: string;
  sections: FooterSection[];
  social_media: SocialMedia[];
  show_social_media: boolean;
}

const defaultFooterSettings: FooterSettings = {
  company_name: 'AssuranceDO.fr',
  description: 'Votre guide complet pour comprendre et souscrire votre assurance Dommages-Ouvrage. Conseils d\'experts et accompagnement personnalisé.',
  email: 'contact@assurancedo.fr',
  copyright_text: '© {year} AssuranceDO.fr - 24Thermidor. Tous droits réservés. Site d\'information sur l\'assurance Dommages-Ouvrage. SIRET: 878493907',
  show_social_media: true,
  social_media: [
    { platform: "X", url: "https://x.com/assurancedo", enabled: true },
    { platform: "Instagram", url: "https://instagram.com/assurancedo", enabled: false },
    { platform: "LinkedIn", url: "https://linkedin.com/company/assurancedo", enabled: false },
    { platform: "Facebook", url: "https://facebook.com/assurancedo", enabled: false }
  ],
  sections: [
    {
      title: 'Navigation',
      links: [
        { text: 'Accueil', url: '/' },
        { text: 'Guides', url: '/guides' },
        { text: 'FAQ', url: '/faq' },
        { text: 'Parler de mon projet', url: '/contact' }
      ]
    },
    {
      title: 'Ressources',
      links: [
        { text: 'Tous nos guides', url: '/guides' },
        { text: 'Questions fréquentes', url: '/faq' },
        { text: 'Parler de mon projet', url: '/contact' },
        { text: 'À propos', url: '/a-propos' }
      ]
    },
    {
      title: 'Légal',
      links: [
        { text: 'À propos', url: '/a-propos' },
        { text: 'Mentions légales', url: '/mentions-legales' },
        { text: 'Politique de confidentialité', url: '/politique-confidentialite' }
      ]
    },
    {
      title: 'Contact',
      links: [
        { text: 'contact@assurancedo.fr', url: 'mailto:contact@assurancedo.fr' },
        { text: 'Formulaire de contact', url: '/contact' }
      ]
    }
  ]
};

export const useFooterSettings = () => {
  const [settings, setSettings] = useState<FooterSettings>(defaultFooterSettings);
  const [isLoading, setIsLoading] = useState(true);
  const { toast } = useToast();

  useEffect(() => {
    loadSettings();
  }, []);

  const loadSettings = async () => {
    try {
      const { data, error } = await supabase
        .from('site_settings')
        .select('value')
        .eq('key', 'footer_settings')
        .maybeSingle();

      if (error) {
        console.error('Error loading footer settings:', error);
        toast({
          title: "Erreur",
          description: "Impossible de charger les paramètres du footer",
          variant: "destructive"
        });
        return;
      }

      if (data?.value) {
        const rawSettings = data.value as any;
        // Use data directly from backend
        const loadedSettings: FooterSettings = {
          company_name: rawSettings.company_name || defaultFooterSettings.company_name,
          description: rawSettings.description || defaultFooterSettings.description,
          email: rawSettings.email || defaultFooterSettings.email,
          copyright_text: rawSettings.copyright_text || defaultFooterSettings.copyright_text,
          show_social_media: rawSettings.show_social_media ?? defaultFooterSettings.show_social_media,
          social_media: rawSettings.social_media || defaultFooterSettings.social_media,
          sections: rawSettings.sections?.map((section: any) => ({
            title: section.title,
            links: section.links?.map((link: any) => ({
              text: link.text || link.label,  // Support both formats
              url: link.url
            })) || []
          })) || defaultFooterSettings.sections
        };
        setSettings(loadedSettings);
      }
    } catch (error) {
      console.error('Error in loadSettings:', error);
    } finally {
      setIsLoading(false);
    }
  };

  return {
    settings,
    isLoading,
    refetch: loadSettings
  };
};